import React, { useMemo } from 'react';
import { ColumnConfig, DataTable } from '../../components/Table';
import { AuditEvent } from '../../components/services/auditService';
import OutcomeBadge from './OutcomeBadge';
import { actorDisplayName, auditRowId, describeTarget, describeTargetDetail, formatAuditTime, formatAuditTimePrecise } from './auditFormat';
import { actionDisplayName } from './useAuditActions';

interface ActivityLogTableProps {
  items: AuditEvent[];
  showTenantColumn: boolean;
  /** tenantId (lowercase) -> name, from Tenant Management; unknown ids are shown as-is. */
  tenantNames?: Record<string, string>;
  /** Action code -> customer-facing name (GET auditlog/actions); unknown codes are shown as-is. */
  actionNames?: Record<string, string>;
  /** Opens the event detail. Omitted = rows are not clickable (only Globaladmin gets the detail view). */
  onSelect?: (event: AuditEvent) => void;
  /**
   * Globaladmin: Target counts the other changes ("+5 more") and its tooltip lists them all. Everyone else sees only
   * the main record, with the same text as the tooltip.
   */
  fullTarget?: boolean;
}

const UserCell = ({ event }: { event: AuditEvent }) => {
  const name = actorDisplayName(event.actor);
  const email = event.actor?.email;
  return (
    <div className="leading-tight min-w-0" title={email || name}>
      <div className="truncate">{name}</div>
      {email && email !== name && <div className="truncate text-xs text-gray-400">{email}</div>}
    </div>
  );
};

const truncated = (text: string, title = text) => (
  <span className="block truncate" title={title}>
    {text}
  </span>
);

// Cross-tenant search only: which tenant's log the row belongs to - its name when known, else the lowercase GUID
// (or "Global" for platform-level events). The id is always in the tooltip.
const tenantColumn = (tenantNames: Record<string, string>): ColumnConfig => ({
  key: 'tenantKey',
  header: 'Tenant',
  width: '1.2fr',
  render: (_value, event: AuditEvent) =>
    event.tenantKey === 'global' ? truncated('Global', 'global') : truncated(tenantNames[event.tenantKey] || event.tenantKey, event.tenantKey),
});

// Newest first comes from the API (paged server-side); there is no client-side sorting, which would only reorder the current page.
// Column order: who (User), what (Action), on what (Target, and Tenant in the cross-tenant view), where (Service),
// the result (Outcome), then when (Time).
export const buildActivityLogColumns = (
  showTenantColumn: boolean,
  tenantNames: Record<string, string> = {},
  actionNames: Record<string, string> = {},
  fullTarget = false,
): ColumnConfig[] => [
  { key: 'actor', header: 'User', width: '1.3fr', render: (_value, event: AuditEvent) => <UserCell event={event} /> },
  { key: 'action', header: 'Action', width: '1.2fr', render: (_value, event: AuditEvent) => truncated(actionDisplayName(event.action, actionNames), event.action) },
  {
    key: 'target',
    header: 'Target',
    width: '1.4fr',
    render: (_value, event: AuditEvent) =>
      fullTarget ? truncated(describeTarget(event), describeTargetDetail(event)) : truncated(describeTarget(event, { countOthers: false })),
  },
  ...(showTenantColumn ? [tenantColumn(tenantNames)] : []),
  { key: 'service', header: 'Service', width: '140px', render: (_value, event: AuditEvent) => truncated(event.service) },
  { key: 'outcome', header: 'Outcome', width: '120px', render: (_value, event: AuditEvent) => <OutcomeBadge outcome={event.outcome} /> },
  {
    key: 'timestampUtc',
    header: 'Time',
    width: '190px',
    render: (_value, event: AuditEvent) => <span title={formatAuditTimePrecise(event.timestampUtc)}>{formatAuditTime(event.timestampUtc)}</span>,
  },
];

const NO_NAMES: Record<string, string> = {};

const ActivityLogTable = ({
  items,
  showTenantColumn,
  tenantNames = NO_NAMES,
  actionNames = NO_NAMES,
  onSelect,
  fullTarget = false,
}: ActivityLogTableProps) => {
  // DataTable lays rows out as CSS grid cells; minWidth 0 lets long text truncate instead of widening the column.
  const columns = useMemo(
    () =>
      buildActivityLogColumns(showTenantColumn, tenantNames, actionNames, fullTarget).map((column) => ({
        ...column,
        cellStyle: { minWidth: 0, overflow: 'hidden', ...column.cellStyle },
      })),
    [showTenantColumn, tenantNames, actionNames, fullTarget],
  );

  return (
    <DataTable
      columns={columns}
      data={items}
      getRowId={(event: AuditEvent) => auditRowId(event)}
      onRowClick={onSelect ? (event: AuditEvent) => onSelect(event) : undefined}
      rowStyle={{ fontSize: '14px' }}
    />
  );
};

export default ActivityLogTable;
